import { useCallback, useMemo } from "react";
import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { ActivityFeedDocument } from "#app/lib/data";
import type { ActivityFeedRow } from "#app/widgets/definitions/activity-list/use-activity-list";

/** Newest first — module-level so the query variables stay referentially stable. */
const ORDER_BY = { creationDate: "desc" } as const;

/**
 * One member's own actions and notes, newest first: the shared `ActivityFeed`
 * query narrowed by actor (`createdBy`), so the rows are the ones a project's
 * timeline shows for that person, on the projects the viewer may see.
 * `limit` rows per page; `loadMore` appends the next one.
 */
export function useMemberActivity(
  userId: string | null,
  limit: number,
): {
  rows: ActivityFeedRow[];
  initialLoading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
} {
  const variables = useMemo(
    () => ({ where: { createdBy: { eq: userId ?? "" } }, limit, orderBy: ORDER_BY }),
    [userId, limit],
  );
  const { data, loading, fetchMore, networkStatus } = useQuery(ActivityFeedDocument, {
    variables,
    skip: !userId,
    notifyOnNetworkStatusChange: true,
  });
  const rows = useMemo(
    () => (data?.activities?.edges ?? []).map((e) => e.node) as ActivityFeedRow[],
    [data],
  );
  const loadingMore = networkStatus === NetworkStatus.fetchMore;
  const hasMore = rows.length < (data?.activities?.total ?? 0);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    void fetchMore({
      variables: { skip: rows.length },
      // The activities field has no merge function (windowed tables need
      // replacement), so the pages accumulate in this query's own view.
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.activities) return prev;
        if (!prev?.activities) return fetchMoreResult;
        return {
          ...prev,
          activities: {
            ...fetchMoreResult.activities,
            edges: [...prev.activities.edges, ...fetchMoreResult.activities.edges],
          },
        };
      },
    });
  }, [fetchMore, loading, loadingMore, hasMore, rows.length]);

  return { rows, initialLoading: loading && !data, loadingMore, hasMore, loadMore };
}
